import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import { News } from 'src/typings';
import { environment } from 'src/environments/environment';
import { NewsService } from '../news.service';

@Component({
  selector: 'app-create',
  templateUrl: './create.component.html',
  styleUrls: ['./create.component.scss']
})
export class CreateComponent implements OnInit {
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  news?: News;
  imgName?: string;
  loading = false;
  saving = false;
  uploading = false;
  generating = false;
  submitted = false;
  private saved = false;

  newsForm = this.fb.group({
    title: ['', [Validators.required, Validators.maxLength(255)]],
    text: ['', [Validators.required]],
    active: [false]
  });

  constructor(
    private newsService: NewsService,
    private fb: UntypedFormBuilder,
    private notificationService: NotificationService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  async ngOnInit() {
    const rawId = this.route.snapshot.paramMap.get('id');
    if (!rawId) return undefined;
    const id = Number(rawId);
    if (!Number.isInteger(id) || id < 1) {
      await this.router.navigate(['/news']);
      return undefined;
    }
    this.loading = true;
    try {
      this.news = await firstValueFrom(this.newsService.getOne(id));
      this.imgName = this.news.img;
      this.newsForm.patchValue({
        title: this.news.title,
        text: this.news.text,
        active: this.news.active
      });
      this.newsForm.markAsPristine();
    } catch {
      this.notificationService.showError('Не удалось загрузить новость');
      await this.router.navigate(['/news']);
    } finally {
      this.loading = false;
    }
    return undefined;
  }

  get img() {
    return this.imgName ? `${environment.BACKEND}/image/${this.imgName}` : undefined;
  }

  get hasUnsavedChanges() {
    return !this.saved && (this.newsForm.dirty || (!!this.imgName && this.imgName !== this.news?.img));
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: BeforeUnloadEvent) {
    if (this.hasUnsavedChanges) event.preventDefault();
  }

  async back() {
    if (this.hasUnsavedChanges && !window.confirm('Уйти без сохранения изменений?')) return;
    await this.router.navigate(['/news']);
  }

  loadImg() {
    if (!this.uploading && !this.generating) this.fileInput?.nativeElement.click();
  }

  async handleFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || this.uploading) return;
    if (!file.type.startsWith('image/')) return this.notificationService.warning('Выберите файл изображения');
    this.uploading = true;
    try {
      this.imgName = await this.newsService.uploadImage(file);
    } catch {
      this.notificationService.showError('Не удалось загрузить изображение');
    } finally {
      this.uploading = false;
      input.value = '';
    }
  }

  async generateImage() {
    const title = String(this.newsForm.value.title || '').trim();
    const text = String(this.newsForm.value.text || '').trim();
    if (!title || !text) return this.notificationService.warning('Сначала заполните заголовок и текст');
    if (this.generating) return;
    this.generating = true;
    try {
      const result = await firstValueFrom(this.newsService.generateImage({ title, text }));
      this.imgName = result.img;
      this.notificationService.notice('Изображение сгенерировано');
    } catch (error) {
      const message =
        typeof error === 'string'
          ? error
          : (error as { message?: string })?.message;
      this.notificationService.showError(
        message ? `Не удалось сгенерировать изображение: ${message}` : 'Не удалось сгенерировать изображение'
      );
    } finally {
      this.generating = false;
    }
  }

  async save() {
    if (this.saving || this.uploading || this.generating) return;
    this.submitted = true;
    if (this.newsForm.invalid) {
      this.newsForm.markAllAsTouched();
      return this.notificationService.warning('Заполните заголовок и текст');
    }
    if (!this.imgName) return this.notificationService.warning('Добавьте изображение');

    this.saving = true;
    const payload = {
      img: this.imgName,
      title: String(this.newsForm.value.title).trim(),
      text: String(this.newsForm.value.text).trim(),
      active: !!this.newsForm.value.active
    };
    try {
      if (this.news) await firstValueFrom(this.newsService.update({ id: this.news.id, ...payload }));
      else await firstValueFrom(this.newsService.create(payload));
      this.saved = true;
      this.notificationService.notice(this.news ? 'Новость обновлена' : 'Новость создана');
      await this.router.navigate(['/news']);
    } catch {
      this.notificationService.showError('Не удалось сохранить новость');
    } finally {
      this.saving = false;
    }
  }
}
